<template>
    <div class="modal fade" id="stock_transfer_modal" ref="modalEl" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-560px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ $t('pages.stock.movement.transferTitle') }}</h2>
                    <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1"><inline-svg src="/media/icons/duotune/arrows/arr061.svg"/></span>
                    </div>
                </div>

                <el-form @submit.prevent="onSubmit" :model="form" ref="formRef">
                    <div class="modal-body py-8 px-lg-10">
                        <div class="std-grid">
                            <div class="std-field">
                                <label class="std-label std-label--required">{{ $t('pages.stock.movement.from') }}</label>
                                <el-select v-model="form.from_warehouse_id" class="w-100"
                                    :placeholder="$t('pages.stock.location.warehousePlaceholder')"
                                    filterable>
                                    <el-option v-for="loc in locations" :key="loc.warehouse_id"
                                        :value="loc.warehouse_id"
                                        :label="warehouseLabel(loc)"/>
                                </el-select>
                            </div>
                            <div class="std-field">
                                <label class="std-label std-label--required">{{ $t('pages.stock.movement.to') }}</label>
                                <el-select v-model="form.to_warehouse_id" class="w-100"
                                    :placeholder="$t('pages.stock.location.warehousePlaceholder')"
                                    filterable>
                                    <el-option v-for="w in availableTargetWarehouses" :key="w.id"
                                        :value="w.id"
                                        :label="targetWarehouseLabel(w)"/>
                                </el-select>
                            </div>
                        </div>

                        <div class="std-field">
                            <label class="std-label std-label--required">{{ $t('pages.stock.movement.quantity') }}</label>
                            <el-input v-model="form.quantity"
                                oninput="value=value.replace(/[^0-9.]/g,'')"
                                :placeholder="$t('pages.stock.movement.quantityPlaceholder')"/>
                            <span v-if="exceedsSource && !form.allow_negative" class="std-hint std-hint--err">
                                {{ $t('pages.stock.errors.insufficient_stock') }}
                            </span>
                        </div>

                        <div class="std-field">
                            <label class="std-toggle-wrap">
                                <input type="checkbox" v-model="form.allow_negative"/>
                                <span class="std-toggle-pill"></span>
                                <span class="std-toggle-text">
                                    <span class="std-toggle-title">{{ $t('pages.stock.movement.allowNegative') }}</span>
                                    <span class="std-toggle-hint">{{ $t('pages.stock.movement.allowNegativeHint') }}</span>
                                </span>
                            </label>
                        </div>

                        <div class="std-field">
                            <label class="std-label">{{ $t('pages.stock.movement.note') }}</label>
                            <el-input v-model="form.note" type="textarea" :rows="2"
                                :placeholder="$t('pages.stock.movement.notePlaceholder')"/>
                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal" :disabled="loading">
                            {{ $t('common.cancel') }}
                        </button>
                        <button type="submit" class="btn btn-primary"
                            :disabled="!canSubmit || loading"
                            :data-kt-indicator="loading ? 'on' : null">
                            <span v-if="!loading" class="indicator-label">{{ $t('common.save') }}</span>
                            <span v-if="loading" class="indicator-progress">
                                {{ $t('messages.wait') }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
function emptyForm() {
    return {
        from_warehouse_id: null,
        to_warehouse_id: null,
        quantity: '',
        allow_negative: false,
        note: '',
    };
}

export default {
    name: "StockTransferDialog",
    props: {
        stockId: { type: [Number, String], required: true },
        locations: { type: Array, default: () => [] },
    },
    emits: ['created'],
    data() {
        return {
            form: emptyForm(),
            loading: false,
            warehouses: [],
        };
    },
    computed: {
        sourceLocation() {
            return this.locations.find(l => l.warehouse_id === this.form.from_warehouse_id) || null;
        },
        availableTargetWarehouses() {
            return this.warehouses.filter(w => w.id !== this.form.from_warehouse_id);
        },
        exceedsSource() {
            const qty = parseFloat(this.form.quantity);
            const source = parseFloat(this.sourceLocation?.current_quantity || 0);
            return qty > 0 && qty > source;
        },
        canSubmit() {
            const qty = parseFloat(this.form.quantity);
            if (!(qty > 0)) return false;
            if (!this.form.from_warehouse_id || !this.form.to_warehouse_id) return false;
            if (this.form.from_warehouse_id === this.form.to_warehouse_id) return false;
            if (this.exceedsSource && !this.form.allow_negative) return false;
            return true;
        },
    },
    methods: {
        formatQty(q) {
            const n = parseFloat(q);
            if (isNaN(n)) return '0';
            return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        warehouseLabel(loc) {
            const name = loc.warehouse?.name || ('Depo #' + loc.warehouse_id);
            const qty = this.formatQty(loc.current_quantity);
            const def = loc.warehouse?.is_default ? ' · ' + this.$t('pages.cash_register.defaultBadge') : '';
            return `${name}${def} — ${qty}`;
        },
        targetWarehouseLabel(w) {
            const loc = this.locations.find(l => l.warehouse_id === w.id);
            const qty = this.formatQty(loc?.current_quantity || 0);
            const def = w.is_default ? ' · ' + this.$t('pages.cash_register.defaultBadge') : '';
            return `${w.name}${def} — ${qty}`;
        },
        async open() {
            this.form = emptyForm();
            await this.fetchWarehouses();
            this.showModal(this.$refs.modalEl);
        },
        async fetchWarehouses() {
            await this.$store.dispatch('stock/warehouse/get', { page: { pageSize: 9999 } });
            this.warehouses = this.$store.state.stock.warehouse.table?.data || [];
        },
        async onSubmit() {
            if (!this.canSubmit) return;
            this.loading = true;
            const body = {
                from_warehouse_id: this.form.from_warehouse_id,
                to_warehouse_id: this.form.to_warehouse_id,
                quantity: parseFloat(this.form.quantity),
                allow_negative: !!this.form.allow_negative,
            };
            if (this.form.note) body.note = this.form.note;

            try {
                const res = await this.$store.dispatch('stock/movement/transfer', {
                    stockId: this.stockId,
                    formData: body,
                });
                if (res?.status) {
                    this.$message.success(this.$t('messages.saveOk'));
                    this.hideModal(this.$refs.modalEl);
                    this.$emit('created', res.data);
                }
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.std-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 14px;
}

.std-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

@media (max-width: 580px) {
    .std-grid { grid-template-columns: 1fr; }
}

.std-label {
    font-size: 12px;
    font-weight: 600;
    color: #1c1c1e;
}

.std-label--required::after {
    content: ' *';
    color: #ef4444;
}

.std-hint {
    font-size: 11.5px;
    color: #8e8e93;
}

.std-hint--err { color: #ef4444; font-weight: 600; }

html.dark .std-label,
html.midnight .std-label { color: #EAE6E1; }
html.cream .std-label { color: #3D3832; }

html.dark .std-hint,
html.midnight .std-hint { color: #9A9CAE; }
html.cream .std-hint { color: #847C72; }

html.dark .std-hint--err,
html.midnight .std-hint--err { color: #F87171; }

/* Toggle */
.std-toggle-wrap {
    display: inline-flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
    user-select: none;
    width: 100%;
    padding: 10px 12px;
    background: rgba(0, 0, 0, 0.025);
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 9px;
}

.std-toggle-wrap input { display: none; }

.std-toggle-pill {
    flex-shrink: 0;
    margin-top: 2px;
    width: 34px;
    height: 20px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.18);
    position: relative;
    transition: background 0.2s;
}

.std-toggle-pill::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.18s;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
}

.std-toggle-wrap input:checked + .std-toggle-pill {
    background: #d97706;
}

.std-toggle-wrap input:checked + .std-toggle-pill::after {
    transform: translateX(14px);
}

.std-toggle-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.std-toggle-title {
    font-size: 13px;
    font-weight: 600;
    color: #1c1c1e;
}

.std-toggle-hint {
    font-size: 11px;
    color: #8e8e93;
    line-height: 1.4;
}

html.dark .std-toggle-wrap,
html.midnight .std-toggle-wrap {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.06);
}

html.dark .std-toggle-title,
html.midnight .std-toggle-title { color: #EAE6E1; }

html.dark .std-toggle-hint,
html.midnight .std-toggle-hint { color: #9A9CAE; }

html.cream .std-toggle-wrap {
    background: rgba(61, 56, 50, 0.04);
    border-color: rgba(61, 56, 50, 0.08);
}

html.cream .std-toggle-title { color: #3D3832; }
html.cream .std-toggle-hint { color: #847C72; }

/* Modal cancel button — Metronic btn-light tum temalarda override */
.btn.btn-light,
.btn.btn-light:hover,
.btn.btn-light:focus,
.btn.btn-light:active,
.btn.btn-light:focus-visible {
    border-color: rgba(0, 0, 0, 0.08) !important;
    color: #1c1c1e !important;
    box-shadow: none !important;
}

.btn.btn-light,
.btn.btn-light:focus { background-color: rgba(0, 0, 0, 0.05) !important; }
.btn.btn-light:hover,
.btn.btn-light:active { background-color: rgba(0, 0, 0, 0.09) !important; }

html.dark .btn.btn-light,
html.dark .btn.btn-light:hover,
html.dark .btn.btn-light:focus,
html.dark .btn.btn-light:active,
html.midnight .btn.btn-light,
html.midnight .btn.btn-light:hover,
html.midnight .btn.btn-light:focus,
html.midnight .btn.btn-light:active {
    border-color: rgba(255, 255, 255, 0.1) !important;
    color: #EAE6E1 !important;
}

html.dark .btn.btn-light,
html.dark .btn.btn-light:focus,
html.midnight .btn.btn-light,
html.midnight .btn.btn-light:focus { background-color: rgba(255, 255, 255, 0.07) !important; }
html.dark .btn.btn-light:hover,
html.dark .btn.btn-light:active,
html.midnight .btn.btn-light:hover,
html.midnight .btn.btn-light:active { background-color: rgba(255, 255, 255, 0.13) !important; }

html.cream .btn.btn-light,
html.cream .btn.btn-light:hover,
html.cream .btn.btn-light:focus,
html.cream .btn.btn-light:active {
    border-color: rgba(61, 56, 50, 0.1) !important;
    color: #3D3832 !important;
}

html.cream .btn.btn-light,
html.cream .btn.btn-light:focus { background-color: rgba(61, 56, 50, 0.06) !important; }
html.cream .btn.btn-light:hover,
html.cream .btn.btn-light:active { background-color: rgba(61, 56, 50, 0.1) !important; }

</style>
